import h from 'hyperscript';
import isEqual from 'lodash-es/isEqual';
import calendarIcon from '@disy/cadenza-icons/calendar.svg';

import { setupMenuButton } from 'ui/menu/menu';
import { createIconButton } from 'ui/button/button';

import { uniqueId } from 'cadenza/utils/unique-id';
import { on } from 'cadenza/utils/event-util';
import { formatDateWithJavaFormat, parseDateWithJavaFormat } from 'cadenza/utils/date-time/date-format';
import { icon } from 'cadenza/utils/icon/icon';
import {
  getRelativeDateSuggestions,
  SUGGESTION_TYPE
} from 'cadenza/conditions/condition-date-picker/condition-date-picker-suggestions-provider';
import { logger } from 'cadenza/utils/logging';
import { errorAlert } from 'cadenza/alert';
import { calcRelativeStartAndEndDate } from 'cadenza/relative-date-time/calc-relative-start-and-end-date';
import { getRelativeDateTimeOrIntervalLabel } from 'cadenza/relative-date-time/get-relative-date-time-or-interval-label';
import { isAbsoluteDateTimeType, isRelativeDateTimeType } from 'cadenza/conditions/condition-date-picker/date-time-type';

import './condition-date-picker.css';
import i18n from './condition-date-picker.properties';

const COMPONENT_NAME = 'd-condition-date-picker';
const DATE_PICKER_OPENED = {};

/**
 * @typedef {object} DatePickerConfig
 * @property {string} profile - Profile
 * @property {string} [dateFormat] - Date format
 * @property {boolean} relativeDateSuggestions - Include relative dates
 * @property {Date} earliestDate - Earliest date selectable
 * @property {boolean} [hourEnabled] - Enable hours
 * @property {boolean} [isInterval] - Is interval
 * @property {Date} latestDate - Latest date selectable
 * @property {boolean} [minuteEnabled] - Enable minutes
 * @property {boolean} [monthEnabled] - Enable months
 * @property {boolean} [dayEnabled] - Enable days
 * @property {boolean} [secondEnabled] - Enable seconds
 * @property {string} [separator] - Date field separator
 * @property {boolean} [yearEnabled] - Enable years
 * @property {string} inputId - ID of input field
 * @property {boolean} [isRequired] - Required
 * @property {boolean} [autoOpen] - Auto open
 * @property {string} modalStyleClass - CSS class of shown modal dialog
 * @property {string} styleClass - CSS class
 * @property {string} title - title for date picker modal
 */

export class ConditionDatePicker extends HTMLElement {

  _modalStyleClass;
  _styleClass;
  _isInterval;
  _dateFormat;
  _separator;
  _autoOpen;
  _textInputSubmitCancelled;
  _input;
  _earliestDate;
  _latestDate;

  /**
   * @param {DatePickerConfig} options - date picker configuration options
   */
  constructor (
    {
      dateFormat = 'dd.MM.yyyy',
      relativeDateSuggestions,
      earliestDate,
      hourEnabled = false,
      isInterval = false,
      latestDate,
      minuteEnabled = false,
      monthEnabled = true,
      dayEnabled = true,
      secondEnabled = false,
      separator = '-',
      yearEnabled = true,
      inputId,
      isRequired = false,
      autoOpen = false,
      modalStyleClass,
      styleClass,
      title
    } = {}
  ) {
    super();
    this._isInterval = isInterval;
    this._dateFormat = dateFormat;
    this._separator = separator;
    this._autoOpen = autoOpen;
    this._earliestDate = earliestDate;
    this._latestDate = latestDate;
    /* Will be used to track user activity.
    If user types value in the input but then opens dropdown for relative date selection,
    then value will be ignored and submitted if and only if user closed dropdown without selection.
    */
    this._textInputSubmitCancelled = false;

    this._input = h(`input.${COMPONENT_NAME}--input.form-control`, {
      id: inputId || uniqueId(),
      type: 'text',
      placeholder: this._isInterval ? `${this._dateFormat} ${this._separator} ${this._dateFormat}` : this._dateFormat,
      required: isRequired,
      onkeydown: event => {
        // start request on enter
        if (event.keyCode === 13) {
          this._onFinishedInput();
        }
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.stopPropagation(); // Don't close the sidebar
          this._setInputValue(this._values);
        }
      },
      onblur: (event) => {
        if (event.relatedTarget === this._dropdownTrigger) {
          this._textInputSubmitCancelled = true;
          return;
        }
        this._onFinishedInput();
      }
    });

    const enableTime = hourEnabled || minuteEnabled || secondEnabled;

    const dateTimeTypes = isInterval
      ? [ 'dateRange', 'beforeDate', 'afterDate' ]
      : [ enableTime ? 'chooseDateTime' : 'chooseDate' ];

    const dateConfiguration = {
      dateFormat,
      relativeDateSuggestions,
      dateTimeTypes,
      earliestDate,
      enableSeconds: secondEnabled,
      enableTime,
      isInterval,
      latestDate,
      onlyYear: yearEnabled && !monthEnabled,
      monthEnabled,
      dayEnabled,
      separator,
      title
    };

    this._textToRelativeDatesMap = this._mapTextToRelativeDates(dateConfiguration.relativeDateSuggestions);
    this._modalStyleClass = modalStyleClass;
    this._styleClass = styleClass;
    this._dropdownTrigger = createIconButton(icon(calendarIcon), i18n('dialog-trigger.title'), { variant: 'borderless' });

    if (this._autoOpen) {
      on(this._dropdownTrigger, 'click', () => showDatePickerModal(
        this,
        dateConfiguration,
        'chooseDate',
        this._modalStyleClass));
    } else {
      setupMenuButton(
        this._dropdownTrigger,
        () => this._createMenuEntries(dateConfiguration),
        {
          appendTo: this,
          styleClass: `${COMPONENT_NAME}-menu`
        });
    }
  }

  tryToSubmitTextValueIfNeeded () {
    if (this._textInputSubmitCancelled) {
      this._textInputSubmitCancelled = false;
      this._onFinishedInput();
    }
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    if (this._styleClass) {
      this.classList.add(this._styleClass);
    }
    this.append(this._input);
    this.append(this._dropdownTrigger);
  }

  dispatchChange () {
    this.dispatchEvent(new CustomEvent('date-selected'));
  }

  dispatchEditWithoutChange () {
    this.dispatchEvent(new CustomEvent('edit-finished'));
  }

  set disabled (value) {
    this.enabled = !value;
  }

  get disabled () {
    return !this.enabled;
  }

  set enabled (enabled) {
    this._input.disabled = !enabled;
    this._dropdownTrigger.disabled = !enabled;
  }

  get enabled () {
    return !this._input.disabled;
  }

  get values () {
    const values = { ...(this._values || { start: null, end: null }) };
    if (values.start) {
      values.start = values.start.toISOString();
    }
    if (values.end) {
      values.end = values.end.toISOString();
    }
    return values;
  }

  get jsDates () {
    if (this._isInterval) {
      if (this._values && this._values.relativeDate) {
        return calcRelativeStartAndEndDate(this._values.relativeDate);
      } else if (this._values) {
        return [ this._values.start || null, this._values.end || null ];
      } else {
        return [ null, null ];
      }
    } else {
      if (this._values && this._values.relativeDate) {
        return calcRelativeStartAndEndDate(this._values.relativeDate);
      } else if (this._values) {
        return this._values.start || null;
      } else {
        return null;
      }
    }
  }

  set values (values) {
    if (values && (values.start || values.end)) {
      const start = typeof values.start === 'string' ? new Date(values.start) : values.start;
      const end = typeof values.end === 'string' ? new Date(values.end) : values.end;
      this._values = { start, end };
    } else if (values && values.relativeDate) {
      this._values = { relativeDate: values.relativeDate };
    } else {
      this._values = null;
    }

    this._setInputValue(this._values);
  }

  get input () {
    return this._input;
  }

  _setInputValue (values) {
    if (values && values.start && this._earliestDate && values.start < this._earliestDate) {
      values.start = this._earliestDate;
    }

    if (values && values.end && this._latestDate && values.end > this._latestDate) {
      values.end = this._latestDate;
    }

    const formattedValue = this.formatDateValues(values);
    if (formattedValue !== this._input.value) {
      this._input.value = formattedValue;
      if (this.parentElement && this.parentElement.classList.contains('form-group')) {
        /**
         Reset the validity as we do in form-validation.js.
         The alternative is to throw an input/change event when programmatically changing the value of this input (not standard)
         */
        this.parentElement.resetInvalid();
      }
    }
  }

  _mapTextToRelativeDates (relativeDateSuggestions) {
    return relativeDateSuggestions.reduce((map, relativeDate) => {
      const textKey = this.formatDateValues({ relativeDate });
      map[textKey] = relativeDate;
      return map;
    }, {});
  }

  _onFinishedInput () {
    const values = this._parseDateValues(this._input.value);
    const isInvalidDate = x => x && x instanceof Date && isNaN(x);
    if (values && (isInvalidDate(values.start) || isInvalidDate(values.end) || isInvalidDate(values.relativeDate))) {
      this._setInputValue(this._values);
      return;
    }
    const oldValue = this.values;
    this.values = values;
    // only trigger event when date really changed,
    // unfortunately the only reliable way of getting this information is to retrieve both values via getter
    if (!isEqual(oldValue, this.values)) {
      this.dispatchChange();
    } else {
      this.dispatchEditWithoutChange();
    }
  }

  _parseDateValues (value) {
    if (value) {
      value = value.trim();
      if (value.includes(this._separator)) {
        const datesAsString = value.split(this._separator).map(s => s.trim());
        return { start: parseDate(datesAsString[0], this._dateFormat), end: parseDate(datesAsString[1], this._dateFormat) };
      } else {
        if (isRelativeDate(value, Object.keys(this._textToRelativeDatesMap))) {
          return { relativeDate: this._textToRelativeDatesMap[value] };
        } else {
          return { start: parseDate(value, this._dateFormat) };
        }
      }
    }
  }

  formatDateValues (values) {
    const formatDate = date => date ? formatDateWithJavaFormat(date, this._dateFormat) : '';
    let formattedValue = '';
    if (values) {
      const relativeDate = values.relativeDate;
      if (relativeDate) {
        formattedValue = getRelativeDateTimeOrIntervalLabel(relativeDate);
      } else {
        formattedValue += formatDate(values.start);
        if (this._isInterval) {
          formattedValue += ` ${this._separator} ${values.end ? formatDate(values.end) : ''}`;
        }
      }
    }
    return formattedValue;
  }

  _createMenuEntries (dateConfiguration) {
    const menuEntries = [];
    menuEntries.push(...this._getRelativeDateTimeMenuItemGroup(dateConfiguration));
    menuEntries.push(...this._getAbsoluteDateTimeMenuItemGroup(dateConfiguration));
    return menuEntries;
  }

  _getAbsoluteDateTimeMenuItemGroup (dateConfiguration) {
    const absoluteDateTimeTypes = dateConfiguration.dateTimeTypes
      .filter(isAbsoluteDateTimeType);
    if (absoluteDateTimeTypes.length) {
      return [ {
        label: i18n('AbsoluteDates'),
        group: absoluteDateTimeTypes
          .map(dateTimeType => ({
            label: i18n(`dateTimeType.<${dateTimeType}>`),
            execute: () => showDatePickerModal(this, dateConfiguration, dateTimeType, this._modalStyleClass)
          }))
      } ];
    }
    return [];
  }

  _getRelativeDateTimeMenuItemGroup (dateConfiguration) {
    const relativeDateSuggestions = getRelativeDateSuggestions(dateConfiguration);
    const relativeDateTimeTypes = dateConfiguration.dateTimeTypes
      .filter(isRelativeDateTimeType);

    if (relativeDateSuggestions.length === 0 && relativeDateTimeTypes.length === 0) {
      return [];
    }

    const relativeDateItemGroup = {
      label: i18n('RelativeDates'),
      group: []
    };

    const presentOrPastSuggestions = relativeDateSuggestions[SUGGESTION_TYPE.RELATIVE_PRESENT_OR_PAST];
    if (presentOrPastSuggestions) {
      relativeDateItemGroup.group.push(
        ...this._createMenuEntriesFromRelativeSuggestions(presentOrPastSuggestions));
    }
    const futureSuggestions = relativeDateSuggestions[SUGGESTION_TYPE.RELATIVE_FUTURE];
    if (futureSuggestions) {
      const futureEntries = this._createMenuEntriesFromRelativeSuggestions(futureSuggestions);
      const entriesToAdd = [ {
        label: i18n('other.dates'),
        subMenu: futureEntries
      } ];
      relativeDateItemGroup.group.push(...entriesToAdd);
    }

    if (relativeDateTimeTypes.length) {
      relativeDateItemGroup.group.push(
        ...relativeDateTimeTypes.map(dateTimeType => ({
          label: i18n(`dateTimeType.<${dateTimeType}>`),
          execute: () => showDatePickerModal(this, dateConfiguration, dateTimeType, this._modalStyleClass)
        })));
    }
    return [ relativeDateItemGroup ];
  }

  _createMenuEntriesFromRelativeSuggestions (suggestions) {
    return suggestions.map(relativeDate => ({
      label: this.formatDateValues({ relativeDate }),
      execute: () => this._setValue({ relativeDate })
    }));
  }

  _setValue (returnValue) {
    if (returnValue && !isEqual(returnValue, DATE_PICKER_OPENED)) {
      this.values = returnValue;
      this.dispatchChange();
    } else if (!isEqual(returnValue, DATE_PICKER_OPENED)) {
      this.tryToSubmitTextValueIfNeeded();
    }
  }

}

function parseDate (dateAsString, format) {
  return parseDateWithJavaFormat(dateAsString, format);
}

function isRelativeDate (value, allRelativeDateTexts) {
  return allRelativeDateTexts.some(relativeDateText => value.includes(relativeDateText));
}

customElements.define(COMPONENT_NAME, ConditionDatePicker);

function showDatePickerModal (datePicker, dateConfiguration, dateTimeType, styleClass) {
  import(/* webpackChunkName: "condition-date-picker-modal" */ './condition-date-picker-modal').then(({ ConditionDatePickerModal }) => {
    const dialog = new ConditionDatePickerModal(
      dateConfiguration,
      dateTimeType,
      datePicker.values,
      styleClass
    );
    dialog.addEventListener('close', () => {
      dialog.remove();
      if (dialog.returnValue) {
        datePicker.values = dialog.returnValue;
        datePicker.dispatchChange();
      } else {
        datePicker.tryToSubmitTextValueIfNeeded();
      }
    });
    document.body.appendChild(dialog);
    dialog.showModal();
  }).catch((error) => {
    logger.error(error);
    errorAlert(i18n('error.dialog.title'), i18n('error.dialog.message'));
  });
}
